/**
 * EJ Insight V2 design tokens.
 *
 * Strict logo-literal palette: the EJ Insight wordmark contains exactly two
 * hues — a bright teal "insight" and a dark teal/slate "ej". This palette
 * uses only those two teals plus neutrals; no tangerine/gold accent.
 *
 * Where ej-tech reaches for a tangerine accent, ejinsight reaches for the
 * darker logo teal (`ink`) to provide contrast within the same hue family.
 *
 * Typography: Fraunces (variable serif, editorial display) + IBM Plex Sans
 * (refined modern body). Distinct from ej-tech's Montserrat/Inter to give
 * the English-language sister publication its own typographic identity.
 *
 * Do NOT import EJTECH_COLORS / CORE_COLORS / LJV2_COLORS. EJ Insight V2 is
 * intentionally its own visual system.
 */

export const EJINSIGHT_COLORS = {
  // brand — logo-literal teals
  primary: "#1EC392", // bright teal — "insight" wordmark
  primaryDark: "#02C28F",
  primarySoft: "#E8F8F2",

  // ink — derived from the dark teal "ej" half of the logo
  ink: "#13343A",
  ink2: "#2D4F52",
  muted: "#6B7E80",
  mutedSoft: "#9CA8AA",

  // surfaces
  surface: "#FFFFFF",
  surfaceAlt: "#F2F9F6", // soft teal-tinted band
  surfaceDark: "#0E2326", // newsletter / footer

  // hairlines
  hairline: "#E1E8E8",
  hairlineSoft: "rgba(19,52,58,0.06)",
  hairlineOnDark: "rgba(255,255,255,0.14)",

  // states
  textOnDark: "#FFFFFF",
  textOnDarkMuted: "rgba(255,255,255,0.62)",
  danger: "#DC2626",
} as const;

export const EJINSIGHT_FONT = {
  display: `Fraunces, Georgia, "Times New Roman", serif`,
  body: `"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif`,
  mono: `"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace`,
} as const;

/**
 * Reusable type recipes. Spread into a `style={{}}` to apply.
 */
export const EJINSIGHT_TYPE = {
  eyebrow: {
    fontFamily: EJINSIGHT_FONT.body,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const,
  },
  eyebrowLg: {
    fontFamily: EJINSIGHT_FONT.body,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.2em",
    textTransform: "uppercase" as const,
  },
  sectionLabel: {
    fontFamily: EJINSIGHT_FONT.body,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
  },
  navLink: {
    fontFamily: EJINSIGHT_FONT.body,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.06em",
  },
  hero: {
    fontFamily: EJINSIGHT_FONT.display,
    fontWeight: 700,
    letterSpacing: "-0.015em",
    lineHeight: 1.08,
  },
  h2: {
    fontFamily: EJINSIGHT_FONT.display,
    fontSize: 32,
    fontWeight: 700,
    letterSpacing: "-0.01em",
    lineHeight: 1.16,
  },
  h3: {
    fontFamily: EJINSIGHT_FONT.display,
    fontSize: 24,
    fontWeight: 600,
    lineHeight: 1.22,
  },
  cardTitle: {
    fontFamily: EJINSIGHT_FONT.display,
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1.32,
  },
  cardTitleLg: {
    fontFamily: EJINSIGHT_FONT.display,
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.24,
  },
  cardTitleXl: {
    fontFamily: EJINSIGHT_FONT.display,
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  body: {
    fontFamily: EJINSIGHT_FONT.body,
    fontSize: 16,
    lineHeight: 1.65,
    fontWeight: 400,
  },
  bodySm: {
    fontFamily: EJINSIGHT_FONT.body,
    fontSize: 14,
    lineHeight: 1.6,
    fontWeight: 400,
  },
  meta: {
    fontFamily: EJINSIGHT_FONT.body,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.4,
  },
  pill: {
    fontFamily: EJINSIGHT_FONT.body,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase" as const,
  },
} as const;

/**
 * Category → palette accent map. Single-hue palette: differentiation comes
 * from tonal shade (bright teal vs. ink teal vs. neutral teal-grey) rather
 * than hue. Keys match the legacy ejinsight category labels (uppercase).
 */
export const EJINSIGHT_CATEGORY_TONE: Record<
  string,
  { fg: string; bg: string }
> = {
  "HONG KONG": { fg: "#13343A", bg: "#E1ECEC" },
  BUSINESS: { fg: "#02C28F", bg: "#E8F8F2" },
  WORLD: { fg: "#13343A", bg: "#F2F9F6" },
  LIFESTYLE: { fg: "#1EC392", bg: "#E8F8F2" },
  FEATURES: { fg: "#13343A", bg: "#E1ECEC" },
  STARTUPS: { fg: "#02C28F", bg: "#E8F8F2" },
  COLUMNISTS: { fg: "#1EC392", bg: "#E8F8F2" },
  OPINION: { fg: "#13343A", bg: "#E1ECEC" },
};
